import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import TopHeader from '../template/TopHeader';
import useWindowSize from '../new-service/useWindowSize';
import useSettingsTabs from './useSettingsTabs';
import SettingsTopNav, { tabIcons } from './SettingsTopNav';
import { HUB_GROUPS, HUB_ITEMS } from './settingsHubIndex';
import { ImportHero } from './ImportData';
import SetupHero, { useSetupFeatured } from '../onboarding/setup/SetupHero';
import { openSetup } from '../onboarding/setup/SetupContext';
import { StripeStatusPill } from './StripeStatusCard';
import './SettingsHub.css';

/**
 * Settings landing page (/settings): the existing settings pages grouped into
 * a short list. Clicking an item opens the page it already is; nothing on the
 * pages themselves changes. Visibility (role, reception mode) comes from
 * useSettingsTabs, the same rules as the tab strip.
 *
 * Search is the app's global palette (Cmd+K / the search icon), which
 * indexes these pages and their sections via settingsHubIndex.
 */

const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path d="M13 13L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);
const ChevronIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 6l6 6-6 6" />
    </svg>
);

// Same hook-in the diary toolbar uses: the palette listens for Cmd/Ctrl+K.
const openGlobalSearch = () => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, ctrlKey: true, bubbles: true }));
};

function SettingsHub({ user, history, ...props }) {
    const { tabs, loading } = useSettingsTabs(user);
    const { width } = useWindowSize();
    const isMobile = width <= 1100;

    // While setup is unfinished it's the featured card, and "Finish setting
    // up" goes straight into it rather than a page that repeats it.
    const setupFeatured = useSetupFeatured();
    const openTab = (tab) => {
        if (tab.id === 'finish-setup' && setupFeatured) { openSetup({}); return; }
        history.push(tab.path);
    };

    const groups = HUB_GROUPS
        .map(g => ({ ...g, items: g.tabIds.map(id => tabs.find(t => t.id === id)).filter(Boolean) }))
        .filter(g => g.items.length > 0);

    const body = (
        <div className="settings-hub">
            <div className="settings-hub-head">
                {!isMobile && <h1 className="settings-hub-title">Settings</h1>}
                <button type="button" className="settings-hub-search-btn" onClick={openGlobalSearch} aria-label="Search settings" title="Search (⌘K)">
                    <SearchIcon />
                    <span>Search settings</span>
                    {!isMobile && <kbd className="settings-hub-search-kbd">{/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K'}</kbd>}
                </button>
            </div>

            {setupFeatured ? <SetupHero /> : <ImportHero compact />}

            {!loading && (
                <div className="settings-hub-groups">
                    {groups.map(g => (
                        <section className="settings-hub-group" key={g.id}>
                            <div className="settings-hub-group-head">
                                <h2>{g.label}</h2>
                                <small>{g.blurb}</small>
                            </div>
                            {g.items.map(tab => {
                                const meta = HUB_ITEMS[tab.id] || {};
                                return (
                                    <button type="button" className="settings-hub-row" key={tab.id} onClick={() => openTab(tab)}>
                                        <span className="settings-hub-row-icon">{tabIcons[tab.id]}</span>
                                        <span className="settings-hub-row-text">
                                            <b>{tab.label}</b>
                                            {meta.description && <span>{meta.description}</span>}
                                        </span>
                                        {tab.id === 'payout-account' && <StripeStatusPill />}
                                        <span className="settings-hub-row-chevron"><ChevronIcon /></span>
                                    </button>
                                );
                            })}
                        </section>
                    ))}
                </div>
            )}
        </div>
    );

    // Mobile keeps the app header (burger menu). Desktop hides it, like the
    // other settings pages, and the hub draws its own title.
    return (
        <div className={`h_n settings-shell${isMobile ? '' : ' catalogue-no-header'}`}>
            <div className="body_container cm_bg_white">
                <TopHeader title="Settings" headerButtonStatus={true} {...props} user={user} history={history} />
                <div className="catalogue-page-wrapper">
                    {/* Same tab strip as every settings page, with "All settings" lit. */}
                    {!isMobile && <SettingsTopNav activeTab="hub" tabs={tabs} onTabChange={(path) => history.push(path)} />}
                    <div className="catalogue-content-container">
                        {body}
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return { ...state };
}

export default withRouter(connect(mapStateToProps)(SettingsHub));
